import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * SFWL-004: the tradeable asset universe is now backend-owned (`useAssets`).
 * `MarketId` is the asset's `symbol`, which is plain string at runtime — the
 * frozen union previously used here drifted out of sync with the seeded set
 * (e.g. YM/RTY/CL never made it in). Validation now happens at the API
 * boundary, not at the type system level.
 */
export type MarketId = string;
export type Timeframe = "1m" | "5m" | "15m" | "1h" | "4h" | "1d" | "1w" | "1M";

export type IndicatorType = "SMA" | "EMA";

export interface IndicatorConfig {
  id: string;
  type: IndicatorType;
  period: number;
  color: string;
  visible: boolean;
}

interface TradeState {
  market: MarketId;
  timeframe: Timeframe;
  indicators: IndicatorConfig[];
  livePrices: Partial<Record<MarketId, number>>;
  setMarket: (market: MarketId) => void;
  setTimeframe: (timeframe: Timeframe) => void;
  addIndicator: (config: IndicatorConfig) => void;
  toggleIndicatorVisibility: (id: string) => void;
  clearIndicators: () => void;
  setLivePrices: (prices: Partial<Record<MarketId, number>>) => void;
}

export const useTradeStore = create<TradeState>()(
  persist(
    (set) => ({
      market: "BTC/USD",
      timeframe: "1h",
      indicators: [],
      livePrices: {},
      setMarket: (market) => set({ market }),
      setTimeframe: (timeframe) => set({ timeframe }),
      addIndicator: (config) =>
        set((state) => ({
          indicators: [...state.indicators.filter((i) => i.id !== config.id), config],
        })),
      toggleIndicatorVisibility: (id) =>
        set((state) => ({
          indicators: state.indicators.map((ind) =>
            ind.id === id ? { ...ind, visible: !ind.visible } : ind
          ),
        })),
      clearIndicators: () => set({ indicators: [] }),
      setLivePrices: (prices) =>
        set((state) => ({ livePrices: { ...state.livePrices, ...prices } })),
    }),
    {
      name: "trade-store",
      partialize: (state) => ({
        market: state.market,
        timeframe: state.timeframe,
        indicators: state.indicators,
      }),
    }
  )
);
